import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import axios from "axios";
import socketService from "../services/socketService";
import {
  Search,
  MoreVertical,
  Send,
  Power,
  LogOut,
  Clock,
  AlertCircle,
  Funnel,
} from "lucide-react";
import ImageMessage from "./ImageMessage";
import ImageUpload from "./ImageUpload";
import {
  Container,
  Sidebar,
  SidebarHeader,
  Logo,
  LogoutButton,
  SearchContainer,
  SearchInput,
  ContactsList,
  Contact,
  Avatar,
  ContactInfo,
  ContactName,
  ContactStatus,
  ContactTimestamp,
  ContactRightSection,
  ChatContainer,
  EmptyChatContainer,
  EmptyChatIcon,
  EmptyChatTitle,
  EmptyChatText,
  ChatHeader,
  ChatInfo,
  ChatName,
  ChatStatus,
  ChatControls,
  ControlButton,
  ToggleContainer,
  ToggleSwitch,
  ToggleInput,
  ToggleSlider,
  ToggleLabels,
  ToggleLabel,
  ChatMessages,
  MessageGroup,
  Message,
  MessageContent,
  MessageMeta,
  MessageTime,
  StatusIndicator,
  DateDivider,
  ChatInput,
  InputField,
  SendButton,
  MobileMenuButton,
  InteractiveMessage,
  ButtonContainer,
  WhatsAppButton,
  InteractiveMessageHeader,
  InteractiveMessageBody,
  InteractiveMessageFooter,
  HistoryButton,
  FilterButton,
  StoreList,
  ModalCloseButton,
} from "./ChatStyles";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useWhatsApp } from "../context/WhatsAppContext";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  width: 400px;
  max-width: 90%;
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 20px;
  color: #333;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CancelButton = styled.button`
  background-color: #f8f9fa;
  color: #333;
  border: 1px solid #ddd;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background-color: #e9ecef;
  }
`;

const ChatInterface = ({ brandFilter = null, showBrandHeader = false }) => {

  // ===== STATE MANAGEMENT =====
  const [selectedContact, setSelectedContact] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [token, setToken] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isAgentMode, setIsAgentMode] = useState(true);
  const [currentBrand, setCurrentBrand] = useState(null);
  const [brandInfo, setBrandInfo] = useState({ name: "", code: "" });
  const [isInputDisabled, setIsInputDisabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [storeListData, setStoreListData] = useState([])

  // ===== INACTIVITY TIMER STATE =====
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes in seconds
  const [showTimer, setShowTimer] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [inactiveChats, setInactiveChats] = useState(new Set());

  // ===== NAVIGATION ======
  const navigate = useNavigate();
  // ===== REFS =====
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const audioRef = useRef(null);
  const inactivityTimeoutRef = useRef(null);
  const timerIntervalRef = useRef(null);

  const processedMessagesRef = useRef(new Set());
  const sendingMessagesRef = useRef(new Map());
  const messageTimeWindowRef = useRef(new Map());
  const lastNotificationRef = useRef(new Map());
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const API_URL2 = import.meta.env.VITE_API_URL2;

  // ===== MEMOIZED VALUES AND FUNCTIONS =====

  // Memoize brand info to prevent unnecessary re-renders
  const memoizedBrandInfo = useMemo(
    () => brandInfo,
    [brandInfo.name, brandInfo.code]
  );

  // Memoize format timestamp function
  const formatTimestamp12Hour = useCallback((timestamp) => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "";
    }
  }, []);

  // Memoize scroll function
  const scrollToBottom = useCallback(() => {
    const scroll = () => {
      if (messagesEndRef.current) {
        try {
          messagesEndRef.current.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
          });
        } catch (error) {
          console.warn("Scroll error:", error);
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop =
              chatContainerRef.current.scrollHeight;
          }
        }
      } else if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    };

    scroll();
    setTimeout(scroll, 50);
    setTimeout(scroll, 150);
  }, []);

  // Memoize notification function
  const showNotification = useCallback(
    (messageData) => {
      const {
        phoneNumber,
        content,
        body,
        profileName,
        sender,
        brandCode,
        messageType,
        type,
        imageUrl,
        caption,
      } = messageData;

      if (sender === "agent" || sender === "system") return;
      if (currentBrand && brandCode && brandCode !== currentBrand) return;

      let messageContent = "";
      const msgType = messageType || type;

      if (msgType === "image") {
        if (caption && caption.trim()) {
          messageContent = `📷 Image: ${caption.trim()}`;
        } else {
          messageContent = "📷 Image sent";
        }
      } else if (
        msgType === "document" ||
        msgType === "audio" ||
        msgType === "video" ||
        msgType === "sticker" ||
        msgType === "location" ||
        msgType === "contact"
      ) {
        return;
      } else if (msgType === "interactive") {
        messageContent = content || body || "Interactive message";
      } else {
        messageContent = content || body || "New message received";
      }

      const contactName =
        profileName || `User ${phoneNumber?.slice(-5)}` || "Customer";
      const notificationKey = `${phoneNumber}_${Date.now()}`;

      const lastNotificationTime =
        lastNotificationRef.current.get(phoneNumber) || 0;
      const now = Date.now();
      if (now - lastNotificationTime < 3000) {
        return;
      }

      lastNotificationRef.current.set(phoneNumber, now);

      if (audioRef.current) {
        audioRef.current.play().catch((err) => {});
      }

      if ("Notification" in window && Notification.permission === "granted") {
        const brandPrefix = currentBrand ? `[${currentBrand}] ` : "";
        const notification = new Notification(
          `${brandPrefix}New WhatsApp Message!`,
          {
            body: `${contactName}: ${messageContent}`,
            icon: "/favicon.ico",
            tag: notificationKey,
            requireInteraction: false,
            silent: false,
          }
        );

        setTimeout(() => notification.close(), 5000);
        notification.onclick = () => {
          window.focus();
          notification.close();
        };
      }
    },
    [currentBrand]
  );

  // Memoize message signature creation
  const createMessageSignature = useCallback((messageData) => {
    const {
      phoneNumber = "",
      content = "",
      body = "",
      sender = "",
      timestamp = "",
      serverId = "",
      messageId = "",
      id = "",
      brandCode = "",
      messageType = "",
      type = "",
      imageUrl = "",
      caption = "",
    } = messageData;

    if (serverId) return `server_${serverId}`;
    if (messageId) return `server_${messageId}`;
    if (id && !id.startsWith("temp-")) return `server_${id}`;

    const messageContent = content || body || caption || "message";
    const messageTime = new Date(timestamp || Date.now()).getTime();
    const roundedTime = Math.floor(messageTime / 10000) * 10000;

    return `${phoneNumber}_${sender}_${brandCode}_${btoa(
      messageContent
    ).substring(0, 15)}_${roundedTime}_${messageType || type}`;
  }, []);

  // Memoize mark message as processed
  const markMessageAsProcessed = useCallback(
    (messageData) => {
      const signature = createMessageSignature(messageData);
      processedMessagesRef.current.add(signature);
    },
    [createMessageSignature]
  );

  // Memoize clear tracking function
  const clearAllTracking = useCallback(() => {
    processedMessagesRef.current.clear();
    sendingMessagesRef.current.clear();
    messageTimeWindowRef.current.clear();
    lastNotificationRef.current.clear();
  }, []);

  // ===== INACTIVITY TIMER FUNCTIONS =====

  const startInactivityTimer = useCallback((contactPhoneNumber) => {
    clearInactivityTimer();

    if (!contactPhoneNumber) return;

    setTimeLeft(900);
    setShowTimer(false);
    setIsInactive(false);

    setInactiveChats((prev) => {
      const newSet = new Set(prev);
      newSet.delete(contactPhoneNumber);
      return newSet;
    });

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleAutoTransfer(contactPhoneNumber);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    inactivityTimeoutRef.current = setTimeout(() => {
      handleAutoTransfer(contactPhoneNumber);
    }, 900000);
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setShowTimer(false);
    setTimeLeft(900);
  }, []);

  const resetInactivityTimer = useCallback(
    (contactPhoneNumber) => {
      if (
        contactPhoneNumber &&
        selectedContact?.phoneNumber === contactPhoneNumber
      ) {
        startInactivityTimer(contactPhoneNumber);
      }
    },
    [selectedContact?.phoneNumber, startInactivityTimer]
  );

  // ===== MEMOIZED EVENT HANDLERS =====

  const handleContactUpdate = useCallback(
    (data) => {
      if (currentBrand && data.brandCode && data.brandCode !== currentBrand) {
        return;
      }

      if (!data.phoneNumber) {
        return;
      }

      setContacts((prev) => {
        return prev.map((contact) => {
          if (contact.phoneNumber === data.phoneNumber) {
            return {
              ...contact,
              ...data,
              timestamp: formatTimestamp12Hour(
                data.timestamp || contact.timestamp
              ),
              unread: !data.isRead,
              unreadCount: data.isRead ? 0 : contact.unreadCount,
              brandCode: data.brandCode || contact.brandCode || currentBrand,
            };
          }
          return contact;
        });
      });
    },
    [currentBrand, formatTimestamp12Hour]
  );

  const handleMessageStatusUpdate = useCallback(
    (data) => {
      if (!selectedContact || data.phoneNumber !== selectedContact.phoneNumber)
        return;
      if (!data.status || (!data.messageId && !data.serverId)) return;

      setChatHistory((prev) => {
        return prev.map((msg) => {
          const matchesId =
            msg.serverId === data.messageId ||
            msg.serverId === data.serverId ||
            msg.id === data.messageId ||
            msg.id === data.serverId;

          if (matchesId) {
            return { ...msg, status: data.status };
          }
          return msg;
        });
      });
    },
    [selectedContact?.phoneNumber]
  );

  const handleAutoTransfer = useCallback(
    async (contactPhoneNumber) => {
      try {
        clearInactivityTimer();

        // console.log(`[AutoTransfer] ✅ Disabling input for: ${contactPhoneNumber}`);

        if (selectedContact?.phoneNumber === contactPhoneNumber) {
          setIsInputDisabled(true);
        }

        // console.log(`[AutoTransfer] ✅ Input disabled for current chat`);
      } catch (error) {
        console.error("[AutoTransfer] Error:", error);
      }
    },
    [selectedContact?.phoneNumber, clearInactivityTimer]
  );

  useEffect(() => {
    if (!token || !currentBrand) return;

    const handleInputDisabled = (data) => {
      if (selectedContact && data.phoneNumber === selectedContact.phoneNumber) {
        setIsInputDisabled(true);
        // console.log(`[Socket] Input disabled for ${data.phoneNumber}`);
      }
    };

    const handleInputEnabled = (data) => {
      if (selectedContact && data.phoneNumber === selectedContact.phoneNumber) {
        setIsInputDisabled(false);
        // console.log(`[Socket] Input enabled for ${data.phoneNumber}`);
      }
    };

    socketService.on("chatInputDisabled", handleInputDisabled);
    socketService.on("chatInputEnabled", handleInputEnabled);

    return () => {
      socketService.off("chatInputDisabled", handleInputDisabled);
      socketService.off("chatInputEnabled", handleInputEnabled);
    };
  }, [selectedContact?.phoneNumber, token, currentBrand]);

  const handleRequestAgent = useCallback(async () => {
    if (!selectedContact) return;

    try {
      // ✅ SIMPLE: Just re-enable input
      setIsInputDisabled(false);

      // Restart inactivity timer
      startInactivityTimer(selectedContact.phoneNumber);

      // console.log(`[RequestAgent] ✅ Input re-enabled for ${selectedContact.phoneNumber}`);
    } catch (error) {
      console.error("[RequestAgent] Error:", error);
    }
  }, [selectedContact?.phoneNumber, startInactivityTimer]);

  // ===== MEMOIZED INCOMING MESSAGE HANDLER =====

  const handleIncomingMessage = useCallback(
    (data) => {
      if (currentBrand && data.brandCode && data.brandCode !== currentBrand) {
        return;
      }

      if (isLoadingHistory) {
        return;
      }

      if (data.sender === "agent") {
        for (const [
          tempId,
          sendingData,
        ] of sendingMessagesRef.current.entries()) {
          if (
            sendingData.content === (data.content || data.body) &&
            sendingData.phoneNumber === data.phoneNumber &&
            Math.abs(
              new Date(data.timestamp).getTime() - sendingData.timestamp
            ) < 15000
          ) {
            return;
          }
        }
      }

      if (
        data.currentState !== "connect_agent" &&
        data.currentState !== "inactive_agent"
      ) {
        return;
      }

      if (!data.phoneNumber || !data.sender) {
        return;
      }

      let messageContent = "";
      const msgType = data.messageType || data.type;

      if (msgType === "image") {
        if (data.caption && data.caption.trim()) {
          messageContent = `📷 ${data.caption.trim()}`;
        } else {
          messageContent = "📷 Photo";
        }
      } else if (
        msgType === "document" ||
        msgType === "audio" ||
        msgType === "video" ||
        msgType === "sticker" ||
        msgType === "location" ||
        msgType === "contact"
      ) {
        return;
      } else {
        messageContent = (data.content || data.body || "").trim();
      }

      if (data.sender === "user") {
        resetInactivityTimer(data.phoneNumber);
        showNotification({
          ...data,
          content: messageContent,
        });
      }

      setContacts((prevContacts) => {
        const existingContactIndex = prevContacts.findIndex(
          (contact) => contact.phoneNumber === data.phoneNumber
        );

        let updatedContacts;

        if (existingContactIndex !== -1) {
          updatedContacts = prevContacts.map((contact, index) => {
            if (index === existingContactIndex) {
              const isCurrentlySelected =
                selectedContact?.phoneNumber === data.phoneNumber;
              const isUserMessage = data.sender === "user";

              return {
                ...contact,
                lastMessage: messageContent || contact.lastMessage,
                timestamp: formatTimestamp12Hour(
                  data.timestamp || new Date().toISOString()
                ),
                lastInteractionAt: data.timestamp || new Date().toISOString(),
                unread:
                  !isCurrentlySelected &&
                  isUserMessage &&
                  data.currentState === "connect_agent",
                unreadCount:
                  !isCurrentlySelected &&
                  isUserMessage &&
                  data.currentState === "connect_agent"
                    ? (contact.unreadCount || 0) + 1
                    : isCurrentlySelected
                    ? 0
                    : contact.unreadCount || 0,
                profileName: data.profileName || contact.profileName,
                name: data.profileName || contact.name,
                sortTimestamp: data.timestamp || new Date().toISOString(),
                brandCode: data.brandCode || contact.brandCode || currentBrand,
                currentState: data.currentState || contact.currentState,
                isInactive:
                  data.currentState === "inactive_agent" || contact.isInactive,
              };
            }
            return contact;
          });
        } else {
          const newContact = {
            phoneNumber: data.phoneNumber,
            profileName:
              data.profileName || `User ${data.phoneNumber.slice(-5)}`,
            name: data.profileName || `User ${data.phoneNumber.slice(-5)}`,
            lastMessage: messageContent || "Customer requesting human agent",
            timestamp: formatTimestamp12Hour(
              data.timestamp || new Date().toISOString()
            ),
            lastInteractionAt: data.timestamp || new Date().toISOString(),
            unread: data.currentState === "connect_agent",
            unreadCount: data.currentState === "connect_agent" ? 1 : 0,
            currentState: data.currentState || "connect_agent",
            sortTimestamp: data.timestamp || new Date().toISOString(),
            brandCode: data.brandCode || currentBrand,
            isInactive: data.currentState === "inactive_agent",
          };

          updatedContacts = [newContact, ...prevContacts];
        }

        return updatedContacts.sort((a, b) => {
          if (a.unread && !b.unread) return -1;
          if (!a.unread && b.unread) return 1;

          const aTime = new Date(
            a.sortTimestamp || a.lastInteractionAt || 0
          ).getTime();
          const bTime = new Date(
            b.sortTimestamp || b.lastInteractionAt || 0
          ).getTime();
          return bTime - aTime;
        });
      });

      if (selectedContact && data.phoneNumber === selectedContact.phoneNumber) {
        setChatHistory((prevHistory) => {
          const messageId =
            data.serverId ||
            data.messageId ||
            data.id ||
            `msg-${Date.now()}-${Math.random()}`;
          const isDuplicate = prevHistory.some((msg) => {
            if (
              messageId &&
              (msg.id === messageId || msg.serverId === messageId)
            ) {
              return true;
            }

            const msgContent = (msg.content || msg.body || "").trim();
            const dataContent = messageContent;

            if (msgContent === dataContent && msg.sender === data.sender) {
              return true;
            }

            return false;
          });

          if (isDuplicate) {
            return prevHistory;
          }

          const newMessage = {
            ...data,
            id: messageId,
            content: messageContent,
            messageType: data.messageType || data.type || "text",
            buttons: data.buttons || [],
            interactive:
              data.interactive || (data.buttons && data.buttons.length > 0),
            header: data.header,
            body: data.body || data.content,
            footer: data.footer,
            brandCode: data.brandCode || currentBrand,
            imageUrl: data.imageUrl,
            caption: data.caption,
            thumbnailUrl: data.thumbnailUrl,
          };

          setTimeout(() => scrollToBottom(), 100);
          return [...prevHistory, newMessage];
        });
      }
    },
    [
      selectedContact?.phoneNumber,
      isLoadingHistory,
      currentBrand,
      formatTimestamp12Hour,
      showNotification,
      scrollToBottom,
      resetInactivityTimer,
    ]
  );

  // Auto scroll effect - optimized dependencies
  useEffect(() => {
    if (chatHistory.length > 0 && !loading && !isLoadingHistory) {
      scrollToBottom();
    }
  }, [chatHistory.length, loading, isLoadingHistory, scrollToBottom]);

  // Force stop loading timer
  useEffect(() => {
    const maxLoadingTimer = setTimeout(() => {
      if (loading || isLoadingHistory) {
        // console.log('[Chat] Force stopping loading after timeout');
        setLoading(false);
        setIsLoadingHistory(false);
      }
    }, 5000);

    return () => clearTimeout(maxLoadingTimer);
  }, []);

  // Notification permission
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  // Initialize token and brand
  useEffect(() => {
    const storedToken =
      localStorage.getItem("brandToken") ||
      localStorage.getItem("auth_token") ||
      localStorage.getItem("token");

    setToken(storedToken);

    let brandCode =
      brandFilter ||
      localStorage.getItem("brandCode") ||
      localStorage.getItem("selectedBrand");

    // let StoreCode = localStorage.getItem("StoreCode");

    if (!brandCode && !brandFilter) {
      brandCode = "DEFAULT";
      localStorage.setItem("brandCode", brandCode);
    }

    const brandName = localStorage.getItem("brandName") || brandCode;

    setCurrentBrand(brandCode);
    setBrandInfo({ name: brandName, code: brandCode });
    // setCurrentStoreCode(StoreCode);

    if (brandCode) {
      axios.defaults.headers.common["X-Brand-Code"] = brandCode;
    }
    if (storedToken) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${storedToken}`;
    }
  }, [brandFilter]);

  // Socket initialization - optimized
  useEffect(() => {
    if (!token || !currentBrand) return;

    let mounted = true;
    let initialized = false;

    const initializeSocket = () => {
      if (initialized || !mounted) return;

      try {
        initialized = true;
        // console.log('[Chat] Initializing socket connection for brand:', currentBrand);

        socketService.initialize(token);

        const messageEvents = [
          "newMessage",
          "agentRequest",
          "newAgentRequest",
          "agent_request",
          "message",
        ];

        messageEvents.forEach((event) => {
          socketService.on(event, handleIncomingMessage);
        });

        socketService.on("messageStatusUpdate", handleMessageStatusUpdate);
        socketService.on("contactUpdate", handleContactUpdate);
      } catch (error) {
        console.error("[Chat] Socket initialization error:", error);
        initialized = false;
      }
    };

    const timeoutId = setTimeout(initializeSocket, 2000);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);

      if (initialized) {
        const messageEvents = [
          "newMessage",
          "agentRequest",
          "newAgentRequest",
          "agent_request",
          "message",
        ];

        messageEvents.forEach((event) => {
          socketService.off(event, handleIncomingMessage);
        });

        socketService.off("messageStatusUpdate", handleMessageStatusUpdate);
        socketService.off("contactUpdate", handleContactUpdate);
      }
    };
  }, [
    token,
    currentBrand,
    handleIncomingMessage,
    handleMessageStatusUpdate,
    handleContactUpdate,
  ]);

  useEffect(() => {
    if (!token || !currentBrand) return;

    let mounted = true;
    let intervalId = null;

    const fetchContacts = async () => {
      if (!mounted) return;

      try {
        setLoadingContacts(true);

        const currentToken =
          token ||
          localStorage.getItem("brandToken") ||
          localStorage.getItem("token");

        let StoreCode = localStorage.getItem("StoreCode");

        let currentStoreCode = StoreCode === null ? "all" : StoreCode;

        const endpoint = brandFilter
          ? API_URL2 + `/api/brands/contacts/${currentStoreCode}`
          : API_URL2 + `/api/chat/contacts/${currentStoreCode}`;

        const response = await axios.get(endpoint, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
            "X-Brand-Code": currentBrand,
          },
          params: brandFilter
            ? {}
            : {
                brandCode: currentBrand,
                includeInactive: true,
                includeInactiveAgent: true,
              },
        });

        const endpointStore = API_URL2 + "/api/brand/stores";

        const responseStore = await axios.get(endpointStore, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentToken}`,
            "X-Brand-Code": currentBrand,
          },
          params: brandFilter
            ? {}
            : {
                brandCode: currentBrand,
                includeInactive: true,
                includeInactiveAgent: true,
              },
        });

        const filterStoreListData = [
          { storeCode: "all", storeName: "ALL Stores" },
          ...responseStore.data.data.map((item) => ({
            storeCode: item.storeCode,
            storeName: item.storeName,
          })),
        ];

        setStoreListData(filterStoreListData);

        if (!mounted) return;

        if (response.data && response.data.data) {
          const processedContacts = response.data.data

            .filter((contact) => {
              const isBrandMatch =
                !contact.brandCode || contact.brandCode === currentBrand;
              const isRelevantState =
                contact.currentState === "connect_agent" ||
                contact.currentState === "inactive_agent" ||
                contact.isAgentDisabled ||
                (contact.isInactive && contact.lastAgentInteraction);
              return isBrandMatch && isRelevantState;
            })
            .map((contact) => {
              let displayName = "";

              if (contact.profile?.name) {
                displayName = contact.profile.name.trim();
              } else if (contact.profileName) {
                displayName = contact.profileName.trim();
              } else if (contact.name) {
                displayName = contact.name.trim();
              }

              if (
                !displayName ||
                displayName === "undefined" ||
                displayName === "null"
              ) {
                displayName = `User ${contact.phoneNumber.slice(-5)}`;
              }

              const isContactInactive =
                inactiveChats.has(contact.phoneNumber) ||
                contact.isInactive ||
                contact.currentState === "inactive_agent";

              return {
                ...contact,
                name: displayName,
                timestamp: contact.lastInteractionAt
                  ? formatTimestamp12Hour(contact.lastInteractionAt)
                  : contact.timestamp
                  ? formatTimestamp12Hour(contact.timestamp)
                  : "",
                unread: Boolean(
                  contact.unread &&
                    contact.currentState === "connect_agent" &&
                    !isContactInactive
                ),
                unreadCount:
                  contact.unread &&
                  contact.currentState === "connect_agent" &&
                  !isContactInactive
                    ? contact.unreadCount || 1
                    : 0,
                currentState: contact.currentState || "bot",
                brandCode: contact.brandCode || currentBrand,
                sortTimestamp:
                  contact.lastInteractionAt ||
                  contact.timestamp ||
                  new Date(0).toISOString(),
                isInactive: isContactInactive,
              };
            })
            .sort((a, b) => {
              if (!a.isInactive && b.isInactive) return -1;
              if (a.isInactive && !b.isInactive) return 1;
              if (a.unread && !b.unread) return -1;
              if (!a.unread && b.unread) return 1;
              return (
                new Date(b.sortTimestamp).getTime() -
                new Date(a.sortTimestamp).getTime()
              );
            });

          setContacts(processedContacts);
        } else {
          setContacts([]);
        }
      } catch (error) {
        if (!mounted) return;

        if (error.response?.status === 401) {
          localStorage.clear();
          window.location.href = "/brand-login";
        } else {
          setContacts([]);
        }
      } finally {
        if (mounted) {
          setLoadingContacts(false);
        }
      }
    };

    // Initial fetch
    fetchContacts();

    // Set up interval with longer period
    intervalId = setInterval(fetchContacts, 300000);

    return () => {
      mounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [token, currentBrand, brandFilter, showModal]);

  // CRITICAL FIX: Chat history fetch with optimized dependencies
  useEffect(() => {
    if (!selectedContact?.phoneNumber || !currentBrand || !token) {
      setChatHistory([]);
      clearInactivityTimer();
      return;
    }

    let mounted = true;

    const fetchChatHistory = async () => {
      if (!mounted) return;

      try {
        setLoading(true);
        setIsLoadingHistory(true);

        clearAllTracking();

        const currentToken =
          token ||
          localStorage.getItem("brandToken") ||
          localStorage.getItem("token");
        const endpoint = brandFilter
          ? API_URL2 + `/api/brands/messages/${selectedContact.phoneNumber}/30`
          : API_URL2 + `/api/chat/messages/${selectedContact.phoneNumber}/30`;

        // console.log('[Chat] Fetching chat history for:', selectedContact.phoneNumber);

        const response = await axios.get(endpoint, {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "X-Brand-Code": currentBrand,
          },
          params: brandFilter ? {} : { brandCode: currentBrand },
          timeout: 10000,
        });

        if (!mounted) return;

        if (response.data && response.data.data) {
          const processedMessages = [];
          const seenSignatures = new Set();

          const sortedMessages = [...response.data.data]
            .filter((msg) => !msg.brandCode || msg.brandCode === currentBrand)
            .sort(
              (a, b) =>
                new Date(a.timestamp || 0).getTime() -
                new Date(b.timestamp || 0).getTime()
            );

          for (const msg of sortedMessages) {
            const signature = createMessageSignature({
              ...msg,
              brandCode: currentBrand,
            });

            if (seenSignatures.has(signature)) continue;
            seenSignatures.add(signature);

            markMessageAsProcessed({ ...msg, brandCode: currentBrand });

            const processedMessage = {
              ...msg,
              id: signature,
              messageType: msg.messageType || msg.type,
              buttons: msg.buttons || [],
              interactive:
                msg.interactive ||
                (msg.buttons && msg.buttons.length > 0) ||
                msg.messageType === "interactive" ||
                msg.type === "interactive" ||
                msg.header ||
                msg.footer,
              header: msg.header,
              body: msg.body || msg.content,
              footer: msg.footer,
              brandCode: msg.brandCode || currentBrand,
            };
            processedMessages.push(processedMessage);
          }

          if (mounted) {
            setChatHistory(processedMessages);
            setIsAgentMode(selectedContact.currentState === "connect_agent");

            const isContactInactive =
              inactiveChats.has(selectedContact.phoneNumber) ||
              selectedContact.currentState === "inactive_agent";
            setIsInactive(isContactInactive);

            if (
              !isContactInactive &&
              selectedContact.currentState === "connect_agent"
            ) {
              startInactivityTimer(selectedContact.phoneNumber);
            }

            socketService.markAsRead(selectedContact.phoneNumber);

            setContacts((prev) => {
              return prev.map((contact) => {
                if (contact.phoneNumber === selectedContact.phoneNumber) {
                  return { ...contact, unread: false, unreadCount: 0 };
                }
                return contact;
              });
            });

            // console.log('[Chat] Chat history loaded successfully');
          }
        } else {
          if (mounted) {
            setChatHistory([]);
          }
        }
      } catch (error) {
        console.error("[Chat] Error fetching chat history:", error);
        if (mounted) {
          setChatHistory([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setIsLoadingHistory(false);
          setTimeout(() => {
            scrollToBottom();
          }, 100);
        }
      }
    };

    fetchChatHistory();

    return () => {
      mounted = false;
    };
  }, [selectedContact?.phoneNumber, currentBrand, token, brandFilter]); // Removed other dependencies

  // Cleanup effect for timers
  useEffect(() => {
    return () => {
      clearInactivityTimer();
    };
  }, [clearInactivityTimer]);

  // ===== OTHER HANDLERS =====

  const handleContactSelect = useCallback(
    (contact) => {
      setChatHistory([]);
      setLoading(true);
      setIsLoadingHistory(true);

      clearInactivityTimer();

      setSelectedContact(contact);
      setIsInputDisabled(contact.isAgentDisabled || false);

      setIsAgentMode(contact.currentState === "connect_agent");

      const isContactInactive =
        inactiveChats.has(contact.phoneNumber) ||
        contact.currentState === "inactive_agent";
      setIsInactive(isContactInactive);

      setContacts((prev) => {
        return prev.map((c) => {
          if (c.phoneNumber === contact.phoneNumber) {
            return {
              ...c,
              unread: false,
              unreadCount: 0,
            };
          }
          return c;
        });
      });

      socketService.markAsRead(contact.phoneNumber);

      setTimeout(() => {
        scrollToBottom();
      }, 500);
    },
    [inactiveChats, clearInactivityTimer, scrollToBottom]
  );

  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || !selectedContact || !currentBrand || isInactive)
      return;

    const messageContent = inputMessage.trim();
    const timestamp = Date.now();
    const tempId = `temp-${timestamp}-${Math.random()
      .toString(36)
      .slice(2, 9)}`;

    if (
      sendingMessagesRef.current.has(tempId) ||
      Array.from(sendingMessagesRef.current.values()).some(
        (msg) =>
          msg.content === messageContent &&
          msg.phoneNumber === selectedContact.phoneNumber &&
          Math.abs(timestamp - msg.timestamp) < 2000
      )
    ) {
      return;
    }

    sendingMessagesRef.current.set(tempId, {
      content: messageContent,
      phoneNumber: selectedContact.phoneNumber,
      timestamp: timestamp,
      brandCode: currentBrand,
    });

    const optimisticMessage = {
      id: tempId,
      content: messageContent,
      sender: "agent",
      timestamp: new Date(timestamp).toISOString(),
      status: "sending",
      phoneNumber: selectedContact.phoneNumber,
      brandCode: currentBrand,
    };

    setChatHistory((prev) => [...prev, optimisticMessage]);
    setInputMessage("");

    resetInactivityTimer(selectedContact.phoneNumber);

    setTimeout(scrollToBottom, 50);

    try {
      const currentToken =
        token ||
        localStorage.getItem("brandToken") ||
        localStorage.getItem("token");

      const endpoint = brandFilter
        ? API_URL2 + "/api/brands/messages"
        : API_URL2 + "/api/chat/messages";

      const response = await axios.post(
        endpoint,
        {
          phoneNumber: selectedContact.phoneNumber,
          message: messageContent,
          brandCode: currentBrand,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "X-Brand-Code": currentBrand,
          },
        }
      );

      if (response.data?.data?.serverId) {
        const serverId = response.data.data.serverId;
        const serverSignature = `server_${serverId}`;

        const serverMessageData = {
          ...optimisticMessage,
          serverId: serverId,
          messageId: serverId,
        };
        markMessageAsProcessed(serverMessageData);

        setChatHistory((prev) => {
          return prev.map((msg) => {
            if (msg.id === tempId) {
              return {
                ...msg,
                id: serverSignature,
                serverId: serverId,
                status: "sent",
              };
            }
            return msg;
          });
        });
      }
    } catch (error) {
      setChatHistory((prev) => {
        return prev.map((msg) => {
          if (msg.id === tempId) {
            return { ...msg, status: "failed" };
          }
          return msg;
        });
      });

      setInputMessage(messageContent);
      alert("Failed to send message. Please try again.");
    } finally {
      sendingMessagesRef.current.delete(tempId);
    }
  }, [
    inputMessage,
    selectedContact,
    currentBrand,
    token,
    scrollToBottom,
    markMessageAsProcessed,
    brandFilter,
    isInactive,
    resetInactivityTimer,
  ]);

  const handleTransferToBot = useCallback(async () => {
    if (!selectedContact || !currentBrand) return;

    const isConfirmed = window.confirm(
      `Are you sure you want to transfer ${
        selectedContact.name || selectedContact.phoneNumber
      } back to the bot? This will end the agent conversation.`
    );

    if (!isConfirmed) return;

    try {
      const currentToken =
        token ||
        localStorage.getItem("brandToken") ||
        localStorage.getItem("token");

      const endpoint1 = brandFilter
        ? API_URL2 + "/api/brands/messages"
        : API_URL2 + "/api/chat/messages";

      await axios.post(
        endpoint1,
        {
          phoneNumber: selectedContact.phoneNumber,
          message:
            "Thank you for contacting us! You're being transferred back to our automated assistant. Have a great day! 😊",
          brandCode: currentBrand,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "X-Brand-Code": currentBrand,
          },
        }
      );

      const endpoint2 = brandFilter
        ? API_URL2 + "/api/brands/transfer-to-bot"
        : API_URL2 + "/api/chat/transfer-to-bot";

      await axios.post(
        endpoint2,
        {
          phoneNumber: selectedContact.phoneNumber,
          brandCode: currentBrand,
          reason: "manual",
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
            "X-Brand-Code": currentBrand,
          },
        }
      );

      clearInactivityTimer();

      setContacts((prev) =>
        prev.filter(
          (contact) => contact.phoneNumber !== selectedContact.phoneNumber
        )
      );

      setSelectedContact(null);
      setIsInactive(false);
      setIsAgentMode(false);
      clearAllTracking();
    } catch (error) {
      alert("Failed to transfer to bot. Please try again.");
    }
  }, [
    selectedContact,
    currentBrand,
    token,
    clearAllTracking,
    brandFilter,
    clearInactivityTimer,
  ]);

  const handleButtonClick = useCallback(
    async (buttonId, buttonText) => {
      if (!selectedContact || !currentBrand || isInactive) return;

      const selectionMessage = {
        id: `selection-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        content: `Selected: ${buttonText}`,
        sender: "user",
        timestamp: new Date().toISOString(),
        status: "delivered",
        brandCode: currentBrand,
      };

      setChatHistory((prev) => [...prev, selectionMessage]);

      resetInactivityTimer(selectedContact.phoneNumber);

      setTimeout(scrollToBottom, 50);

      try {
        const currentToken =
          token ||
          localStorage.getItem("brandToken") ||
          localStorage.getItem("token");

        await axios.post(
          API_URL2 + "/api/chat/button-response",
          {
            phoneNumber: selectedContact.phoneNumber,
            buttonId: buttonId,
            buttonText: buttonText,
            brandCode: currentBrand,
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
              "X-Brand-Code": currentBrand,
            },
          }
        );
      } catch (error) {
        // Handle error silently
      }
    },
    [
      selectedContact,
      currentBrand,
      token,
      scrollToBottom,
      isInactive,
      resetInactivityTimer,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage]
  );

  const handleLogout = useCallback(() => {
    const isConfirmed = window.confirm(
      "Are you sure you want to logout? You will be redirected to the login page."
    );

    if (!isConfirmed) return;

    try {
      clearAllTracking();
      clearInactivityTimer();

      if (brandFilter) {
        localStorage.removeItem("brandToken");
        localStorage.removeItem("brandCode");
        localStorage.removeItem("brandName");
        localStorage.removeItem("brandTokenExpiry");
        window.location.href = "/brand-login";
      } else {
        localStorage.clear();
        window.location.href = "/login";
      }

      socketService.disconnect();
    } catch (error) {
      window.location.href = brandFilter ? "/brand-login" : "/login";
    }
  }, [clearAllTracking, brandFilter, clearInactivityTimer]);

  // ===== HISTORY INTERFACE =====

  const handleCustomerHistory = () => {
    navigate("/customer-history", {
      state: {
        selectedContact: selectedContact,
      },
    });
  };

  // ===== MEMOIZED COMPUTED VALUES =====

  const filteredContacts = useMemo(() => {
    if (!searchTerm.trim()) return contacts;

    return contacts.filter((contact) => {
      const searchLower = searchTerm.toLowerCase();
      const name = (contact.name || contact.profileName || "").toLowerCase();
      const phone = contact.phoneNumber.toLowerCase();
      const lastMessage = (contact.lastMessage || "").toLowerCase();

      return (
        name.includes(searchLower) ||
        phone.includes(searchLower) ||
        lastMessage.includes(searchLower)
      );
    });
  }, [contacts, searchTerm]);

  const storeFilter = [
    {
      storeName: "store1",
      storeCode: "ELHUEE",
    },
    {
      storeName: "Test store login",
      storeCode: "D1DHOB6TA",
    },
    {
      storeName: "Delhi_Store",
      storeCode: "DEQAA",
    },
    {
      storeName: "Noida Store ",
      storeCode: "D1DHOBKCVV",
    },
    {
      storeName: "Ghaziabad",
      storeCode: "D1DHOBCFTB",
    },
    {
      storeName: "TestStore",
      storeCode: "D1DHOB7TG",
    },
  ];

  const handleStoreSelection = (StoreCode) => {
    localStorage.setItem("StoreCode", StoreCode);
    closeModal();
  };

  const isMessageSent = useCallback(
    (sender) => sender === "agent" || sender === "system",
    []
  );

  const getInitials = useCallback((name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  }, []);

  const renderMessageStatus = useCallback((status) => {
    switch (status) {
      case "sending":
        return <div className="animate-pulse">⏳</div>;
      case "sent":
        return (
          <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
            <path
              d="M1 6l4 4L15 1"
              stroke="#8696a0"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "delivered":
        return (
          <svg width="18" height="12" viewBox="0 0 18 12" fill="none">
            <path
              d="M1 6l4 4L15 1"
              stroke="#8696a0"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6l4 4L20 1"
              stroke="#8696a0"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "read":
        return (
          <svg width="18" height="12" viewBox="0 0 18 12" fill="none">
            <path
              d="M1 6l4 4L15 1"
              stroke="#4fc3f7"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6l4 4L20 1"
              stroke="#4fc3f7"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "failed":
        return <span style={{ color: "#f56565" }}>❌</span>;
      default:
        return null;
    }
  }, []);

  // ===== BRAND-AWARE UI COMPONENTS =====

  const BrandHeader = useCallback(() => {
    if (!showBrandHeader) return null;

    return (
      <div
        style={{
          padding: "16px 20px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e9edef",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
            {memoizedBrandInfo.name} - Agent Dashboard
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: "14px", opacity: 0.9 }}>
            Brand Code: {currentBrand} • Managing WhatsApp conversations
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "white",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          Logout
        </button>
      </div>
    );
  }, [showBrandHeader, memoizedBrandInfo.name, currentBrand, handleLogout]);

  const EmptyStateMessage = useCallback(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "#8696a0",
          padding: "40px 20px",
        }}
      >
        <div
          style={{
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: "#f0f2f5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
          }}
        >
          <svg
            width="60"
            height="60"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#8696a0"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        </div>
        <div
          style={{
            fontWeight: "500",
            marginBottom: "8px",
            color: "#41525d",
            fontSize: "18px",
          }}
        >
          No agent requests for {memoizedBrandInfo.name}
        </div>
        <div
          style={{
            fontSize: "14px",
            color: "#8696a0",
            maxWidth: "80%",
            lineHeight: "1.4",
            textAlign: "center",
          }}
        >
          When customers request to talk with an agent for{" "}
          <strong>{currentBrand}</strong>, they'll appear here
        </div>
        <div
          style={{
            marginTop: "20px",
            padding: "12px 20px",
            background: "white",
            borderRadius: "8px",
            border: "1px solid #e9edef",
            fontSize: "12px",
            color: "#667781",
          }}
        >
          Brand Code: <strong>{currentBrand}</strong>
        </div>
      </div>
    ),
    [memoizedBrandInfo.name, currentBrand]
  );

  // ==== STORE FILTER MODAL ====

  const closeModal = () => {
    setShowModal(false);
  };

  // ===== RENDER =====

  return (
    <Container>
      <BrandHeader />

      <audio ref={audioRef} preload="auto">
        <source src="/notification.mp3" type="audio/mpeg" />
        <source src="/notification.wav" type="audio/wav" />
        <source src="/notification.ogg" type="audio/ogg" />
      </audio>

      <Sidebar $isOpen={sidebarOpen}>
        <SidebarHeader>
          <div>
            <Logo>{memoizedBrandInfo.name || "DHOBICART"}</Logo>
            <div
              style={{
                fontSize: "12px",
                color: "#64748b",
                marginTop: "2px",
                fontWeight: "500",
              }}
            >
              {currentBrand}
            </div>
          </div>
          <LogoutButton onClick={handleLogout}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            <span>Logout</span>
          </LogoutButton>
        </SidebarHeader>

        <SearchContainer>
          <SearchInput>
            <Search size={16} color="#8696a0" />
            <input
              placeholder="Search conversations"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInput>
          <FilterButton onClick={() => setShowModal(true)}>
            <Funnel size={20} />
          </FilterButton>
        </SearchContainer>

        <ContactsList>
          {loadingContacts ? (
            <div
              style={{ padding: "20px", textAlign: "center", color: "#667781" }}
            >
              <div className="animate-pulse">Loading contacts...</div>
            </div>
          ) : filteredContacts.length === 0 ? (
            <EmptyStateMessage />
          ) : (
            filteredContacts.map((contact) => (
              <Contact
                key={`${contact.phoneNumber}-${currentBrand}`}
                $active={
                  selectedContact &&
                  selectedContact.phoneNumber === contact.phoneNumber
                }
                onClick={() => handleContactSelect(contact)}
                style={{
                  backgroundColor: contact.unread
                    ? "#f0f9ff"
                    : contact.isInactive
                    ? "#fef3c7"
                    : "transparent",
                  borderLeft: contact.unread
                    ? "3px solid #25d366"
                    : contact.isInactive
                    ? "3px solid #f59e0b"
                    : "3px solid transparent",
                  fontWeight: contact.unread ? "600" : "normal",
                  transition: "all 0.2s ease",
                  opacity: contact.isInactive ? 0.85 : 1,
                }}
              >
                <Avatar style={{ position: "relative" }}>
                  {getInitials(contact.name)}
                  {contact.unread && (
                    <div
                      style={{
                        position: "absolute",
                        top: "-2px",
                        right: "-2px",
                        width: "12px",
                        height: "12px",
                        backgroundColor: "#25d366",
                        borderRadius: "50%",
                        border: "2px solid white",
                        animation: "pulse 2s infinite",
                      }}
                    />
                  )}
                  {contact.isInactive && (
                    <div
                      style={{
                        position: "absolute",
                        bottom: "-2px",
                        right: "-2px",
                        width: "16px",
                        height: "16px",
                        backgroundColor: "#f59e0b",
                        borderRadius: "50%",
                        border: "2px solid white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Clock size={8} color="white" />
                    </div>
                  )}
                </Avatar>

                <ContactInfo>
                  <ContactName
                    style={{
                      color: contact.unread ? "#111b21" : "#111b21",
                      fontWeight: contact.unread ? "600" : "normal",
                    }}
                  >
                    {contact.profileName ||
                      contact.name ||
                      `User ${contact.phoneNumber.slice(-5)}`}
                    {contact.isInactive && (
                      <span
                        style={{
                          fontSize: "10px",
                          color: "#f59e0b",
                          marginLeft: "6px",
                          fontWeight: "600",
                          background: "#fef3c7",
                          padding: "2px 6px",
                          borderRadius: "10px",
                        }}
                      >
                        15min inactive
                      </span>
                    )}
                  </ContactName>

                  <ContactStatus
                    style={{
                      color: contact.unread ? "#111b21" : "#667781",
                      fontWeight: contact.unread ? "500" : "normal",
                    }}
                  >
                    {contact.isTyping ? (
                      <span style={{ color: "#25d366", fontStyle: "italic" }}>
                        typing...
                      </span>
                    ) : contact.isInactive ? (
                      <span style={{ color: "#f59e0b", fontStyle: "italic" }}>
                        💤 Click to reactivate chat
                      </span>
                    ) : (
                      contact.lastMessage || "No messages yet"
                    )}
                  </ContactStatus>
                </ContactInfo>

                <ContactRightSection>
                  <ContactTimestamp
                    style={{
                      color: contact.unread
                        ? "#25d366"
                        : contact.isInactive
                        ? "#f59e0b"
                        : "#8696a0",
                      fontWeight: contact.unread ? "600" : "normal",
                      fontSize: "12px",
                    }}
                  >
                    {contact.timestamp}
                  </ContactTimestamp>

                  {contact.unread && contact.unreadCount > 0 && (
                    <div
                      style={{
                        backgroundColor: "#25d366",
                        color: "white",
                        minWidth: "20px",
                        height: "20px",
                        borderRadius: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "700",
                        marginTop: "4px",
                        animation: "fadeIn 0.3s ease",
                        boxShadow: "0 2px 4px rgba(37, 211, 102, 0.3)",
                      }}
                    >
                      {contact.unreadCount > 99 ? "99+" : contact.unreadCount}
                    </div>
                  )}
                </ContactRightSection>
              </Contact>
            ))
          )}
        </ContactsList>
      </Sidebar>

      {selectedContact ? (
        <ChatContainer style={{ position: "relative" }}>
          <ChatHeader>
            <MobileMenuButton onClick={() => setSidebarOpen(!sidebarOpen)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </MobileMenuButton>
            <Avatar>{getInitials(selectedContact.name)}</Avatar>
            <ChatInfo>
              <ChatName>
                {selectedContact.profileName ||
                  selectedContact.name ||
                  `User ${selectedContact.phoneNumber.slice(-5)}`}
                {isInactive && (
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#f59e0b",
                      marginLeft: "8px",
                      fontWeight: "500",
                    }}
                  >
                    (Inactive 15min)
                  </span>
                )}
              </ChatName>
              <ChatStatus>{selectedContact.phoneNumber}</ChatStatus>
            </ChatInfo>
            <ChatControls>
              <ToggleContainer>
                <ToggleLabels>
                  <ToggleLabel $active={!isAgentMode}>Bot</ToggleLabel>
                  <ToggleSwitch>
                    <ToggleInput
                      type="checkbox"
                      checked={isAgentMode}
                      onChange={handleTransferToBot}
                      id="agent-toggle"
                      disabled={isInactive}
                    />
                    <ToggleSlider
                      htmlFor="agent-toggle"
                      $isAgent={isAgentMode}
                      style={{
                        opacity: isInactive ? 0.5 : 1,
                      }}
                    />
                  </ToggleSwitch>
                  <ToggleLabel $active={isAgentMode}>Agent</ToggleLabel>
                </ToggleLabels>
                <HistoryButton onClick={handleCustomerHistory}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 36 36"
                    version="1.1"
                    strokeWidth="2"
                    preserveAspectRatio="xMidYMid meet"
                    xmlns="http://www.w3.org/2000/svg"
                    xmlns:xlink="http://www.w3.org/1999/xlink"
                  >
                    <title>history-line</title>
                    <path
                      d="M18,9.83a1,1,0,0,0-1,1v8.72l5.9,4A1,1,0,0,0,24,21.88l-5-3.39V10.83A1,1,0,0,0,18,9.83Z"
                      class="clr-i-outline clr-i-outline-path-1"
                      fill="#fff"
                    ></path>
                    <path
                      d="M18,2A16.09,16.09,0,0,0,4,10.26V5.2a1,1,0,0,0-2,0V14h8.8a1,1,0,0,0,0-2H5.35A14,14,0,1,1,8.58,28.35a1,1,0,0,0-1.35,1.48A16,16,0,1,0,18,2Z"
                      class="clr-i-outline clr-i-outline-path-2"
                      fill="#fff"
                    ></path>
                    <rect x="0" y="0" width="36" height="36" fill-opacity="0" />
                  </svg>
                  <span>History</span>
                </HistoryButton>
              </ToggleContainer>
              <ControlButton>
                <Search size={20} />
              </ControlButton>
              <ControlButton>
                <MoreVertical size={20} />
              </ControlButton>
            </ChatControls>
          </ChatHeader>

          <ChatMessages ref={chatContainerRef}>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  width: "100%",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    border: "4px solid #f3f3f3",
                    borderTop: "4px solid #25d366",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              </div>
            ) : chatHistory.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "#8696a0",
                }}
              >
                <div
                  style={{
                    width: "150px",
                    height: "150px",
                    borderRadius: "50%",
                    background: "#f0f2f5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: "20px",
                  }}
                >
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8696a0"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div
                  style={{
                    fontWeight: "500",
                    marginBottom: "8px",
                    color: "#41525d",
                  }}
                >
                  No messages yet
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#8696a0",
                    maxWidth: "80%",
                    lineHeight: "1.4",
                    textAlign: "center",
                  }}
                >
                  Start the conversation by sending a message to this customer
                </div>
              </div>
            ) : (
              <>
                {chatHistory.map((message, index) => {
                  const showDateDivider =
                    index === 0 ||
                    new Date(message.timestamp).toLocaleDateString() !==
                      new Date(
                        chatHistory[index - 1].timestamp
                      ).toLocaleDateString();

                  return (
                    <React.Fragment key={message.id || `msg-${index}`}>
                      {showDateDivider && (
                        <DateDivider>
                          <span>
                            {new Date(message.timestamp).toLocaleDateString(
                              "en-US",
                              {
                                weekday: "long",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </span>
                        </DateDivider>
                      )}

                      <MessageGroup $sent={isMessageSent(message.sender)}>
                        {message.messageType === "system_inactive" ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              margin: "16px 0",
                            }}
                          >
                            <div
                              style={{
                                background:
                                  "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                                border: "2px solid #f59e0b",
                                borderRadius: "12px",
                                padding: "12px 16px",
                                maxWidth: "80%",
                                textAlign: "center",
                                color: "#92400e",
                                fontSize: "14px",
                                fontWeight: "500",
                                boxShadow: "0 4px 12px rgba(245, 158, 11, 0.2)",
                              }}
                            >
                              <div style={{ marginBottom: "8px" }}>
                                {message.content}
                              </div>
                              <button
                                onClick={handleRequestAgent}
                                style={{
                                  background:
                                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                  border: "none",
                                  color: "white",
                                  padding: "8px 16px",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                }}
                              >
                                🔄 Reactivate Agent Chat
                              </button>
                            </div>
                          </div>
                        ) : message.messageType === "image" ? (
                          <Message $sent={isMessageSent(message.sender)}>
                            <ImageMessage
                              message={message}
                              brandCode={currentBrand}
                            />
                            <MessageMeta>
                              <MessageTime>
                                {formatTimestamp12Hour(message.timestamp)}
                              </MessageTime>
                              {isMessageSent(message.sender) && (
                                <StatusIndicator>
                                  {renderMessageStatus(message.status)}
                                </StatusIndicator>
                              )}
                            </MessageMeta>
                          </Message>
                        ) : message.messageType === "interactive" ||
                          message.interactive === true ||
                          (message.buttons && message.buttons.length > 0) ||
                          message.header ||
                          message.footer ||
                          message.type === "interactive" ? (
                          <InteractiveMessage
                            $sent={isMessageSent(message.sender)}
                          >
                            {message.header && (
                              <InteractiveMessageHeader>
                                {message.header}
                              </InteractiveMessageHeader>
                            )}

                            <InteractiveMessageBody>
                              {message.body ||
                                message.content ||
                                "Please select an option"}
                            </InteractiveMessageBody>

                            {message.buttons && message.buttons.length > 0 && (
                              <ButtonContainer>
                                {message.buttons.map((button, btnIndex) => (
                                  <WhatsAppButton
                                    key={`${button.id}-${btnIndex}`}
                                    onClick={() =>
                                      handleButtonClick(button.id, button.title)
                                    }
                                    disabled={
                                      isMessageSent(message.sender) ||
                                      isInactive
                                    }
                                    style={{
                                      opacity: isInactive ? 0.5 : 1,
                                      cursor: isInactive
                                        ? "not-allowed"
                                        : "pointer",
                                    }}
                                  >
                                    {button.title}
                                  </WhatsAppButton>
                                ))}
                              </ButtonContainer>
                            )}

                            {message.footer && (
                              <InteractiveMessageFooter>
                                {message.footer}
                              </InteractiveMessageFooter>
                            )}

                            <MessageMeta>
                              <MessageTime>
                                {formatTimestamp12Hour(message.timestamp)}
                              </MessageTime>
                              {isMessageSent(message.sender) && (
                                <StatusIndicator>
                                  {renderMessageStatus(message.status)}
                                </StatusIndicator>
                              )}
                            </MessageMeta>
                          </InteractiveMessage>
                        ) : message.messageType === "text" ||
                          !message.messageType ? (
                          <Message $sent={isMessageSent(message.sender)}>
                            <MessageContent>{message.content}</MessageContent>

                            <MessageMeta>
                              <MessageTime>
                                {formatTimestamp12Hour(message.timestamp)}
                              </MessageTime>
                              {isMessageSent(message.sender) && (
                                <StatusIndicator>
                                  {renderMessageStatus(message.status)}
                                </StatusIndicator>
                              )}
                            </MessageMeta>
                          </Message>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: isMessageSent(message.sender)
                                ? "flex-end"
                                : "flex-start",
                              margin: "8px 0",
                            }}
                          >
                            <div
                              style={{
                                background: "#fff3cd",
                                border: "1px solid #ffeaa7",
                                color: "#856404",
                                padding: "8px 12px",
                                borderRadius: "8px",
                                fontSize: "14px",
                                fontStyle: "italic",
                                maxWidth: "70%",
                              }}
                            >
                              ⚠️ Unsupported media type:{" "}
                              {message.messageType || "Unknown"}
                              <div
                                style={{ fontSize: "12px", marginTop: "4px" }}
                              >
                                Only text and images are supported in this chat
                                interface
                              </div>
                            </div>
                          </div>
                        )}
                      </MessageGroup>
                    </React.Fragment>
                  );
                })}

                <div ref={messagesEndRef} style={{ height: 1 }} />
              </>
            )}
          </ChatMessages>

          <ChatInput
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              padding: "12px 16px",
              background: "white",
              borderTop: "1px solid #e9edef",
              gap: "8px",
            }}
          >
            {/* ✅ KEEP ONLY THIS SMALL NOTIFICATION */}
            {isInputDisabled && (
              <div
                style={{
                  position: "absolute",
                  top: "-50px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  background:
                    "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  color: "white",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: "600",
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.4)",
                  zIndex: 10,
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span>💤 Input disabled after 10 min inactivity</span>
                <button
                  onClick={handleRequestAgent}
                  style={{
                    background: "white",
                    color: "#f59e0b",
                    border: "none",
                    padding: "4px 8px",
                    borderRadius: "8px",
                    fontSize: "10px",
                    fontWeight: "700",
                    cursor: "pointer",
                  }}
                >
                  🔄 Enable
                </button>
              </div>
            )}

            {/* Image Upload Component */}
            <ImageUpload
              onImageSend={(imageData) => {
                if (selectedContact && !isInputDisabled) {
                  resetInactivityTimer(selectedContact.phoneNumber);
                
		}

		      fetchChatHistory();
              }}
              selectedContact={selectedContact}
              brandCode={currentBrand}
              disabled={isInputDisabled}
            />

            {/* Input Field */}
            <InputField
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                background: "white",
                border: "1px solid #e9edef",
                borderRadius: "24px",
                padding: "8px 16px",
                minHeight: "40px",
              }}
            >
              <textarea
                placeholder={
                  isInputDisabled
                    ? "💤 Input disabled due to 10 min inactivity - Click Enable button above"
                    : "Type a message"
                }
                value={inputMessage}
                onChange={(e) => {
                  if (!isInputDisabled) {
                    setInputMessage(e.target.value);
                  }
                }}
                onKeyDown={(e) => {
                  if (isInputDisabled) {
                    e.preventDefault();
                    return;
                  }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                onFocus={() => {
                  if (isInputDisabled) {
                    e.target.blur();
                  }
                }}
                rows={1}
                disabled={isInputDisabled}
                style={{
                  flex: 1,
                  border: "none",
                  outline: "none",
                  fontSize: "15px",
                  lineHeight: "20px",
                  resize: "none",
                  fontFamily: "inherit",
                  backgroundColor: "transparent",
                  minHeight: "20px",
                  maxHeight: "120px",
                  overflow: "hidden",
                  padding: "0",
                  opacity: isInputDisabled ? 0.5 : 1,
                  cursor: isInputDisabled ? "not-allowed" : "text",
                  color: isInputDisabled ? "#92400e" : "#1e293b",
                }}
                onInput={(e) => {
                  if (!isInputDisabled) {
                    // Auto-resize textarea
                    e.target.style.height = "auto";
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 120) + "px";
                  }
                }}
              />
            </InputField>

            {/* Send Button */}
            <SendButton
              onClick={() => {
                if (isInputDisabled) {
                  handleRequestAgent();
                } else {
                  handleSendMessage();
                }
              }}
              disabled={false}
              style={{
                background: isInputDisabled
                  ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                  : "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "white",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
              }}
            >
              {isInputDisabled ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: "2px",
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                  <span style={{ fontSize: "8px", fontWeight: "600" }}>
                    Enable
                  </span>
                </div>
              ) : (
                <Send size={20} />
              )}
            </SendButton>
          </ChatInput>
        </ChatContainer>
      ) : (
        <EmptyChatContainer>
          <EmptyChatIcon>
            <svg
              width="100"
              height="100"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#e1e1e1"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </EmptyChatIcon>
          <EmptyChatTitle>
            {memoizedBrandInfo.name} WhatsApp Chat
          </EmptyChatTitle>
          <EmptyChatText>
            Select a contact to start chatting. Connect with your customers
            through {memoizedBrandInfo.name}'s WhatsApp integration.
          </EmptyChatText>

          <div
            style={{
              marginTop: "20px",
              fontSize: "14px",
              color: "#8696a0",
              maxWidth: "450px",
              padding: "20px",
              background: "white",
              borderRadius: "12px",
              border: "1px solid #e9edef",
              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.03)",
            }}
          >
            <p
              style={{
                fontWeight: "500",
                marginBottom: "10px",
                color: "#41525d",
                fontSize: "15px",
              }}
            >
              Brand: {memoizedBrandInfo.name} ({currentBrand})
            </p>
            <p
              style={{ fontSize: "14px", lineHeight: "1.5", color: "#667781" }}
            >
              When customers request to talk with an agent for your brand
              through WhatsApp, they'll appear in the contacts list. You can
              then handle their queries directly from this interface.
            </p>
            <div
              style={{
                marginTop: "12px",
                padding: "8px 12px",
                background: "#f0f9ff",
                borderRadius: "6px",
                fontSize: "12px",
                color: "#0369a1",
              }}
            >
              💡 <strong>Tip:</strong> Chats automatically transfer to bot after
              15 minutes of inactivity
            </div>
          </div>
        </EmptyChatContainer>
      )}
      {showModal && (
        <ModalOverlay>
          <Modal open={open} onClose={closeModal}>
            <>
              <ModalButtons>
                <ModalTitle>Select Store</ModalTitle>
                <ModalCloseButton onClick={closeModal}>X</ModalCloseButton>
              </ModalButtons>
              <StoreList>
                {storeListData.map((store) => (
                  <Contact
                    key={`${store.storeCode}`}
                    onClick={() => handleStoreSelection(store.storeCode)}
                    style={{
                      backgroundColor: "#f0f9ff",
                      borderLeft: "3px solid #25d366",
                      fontWeight: "600",
                      transition: "all 0.2s ease",
                      opacity: 0.85,
                    }}
                  >
                    <Avatar style={{ position: "relative" }}>
                      {getInitials(store.storeName)}
                    </Avatar>

                    <ContactInfo>
                      <ContactName
                        style={{
                          color: "#111b21",
                          fontWeight: "600",
                        }}
                      >
                        {store.storeName}
                      </ContactName>
                    </ContactInfo>
                  </Contact>
                ))}
              </StoreList>
            </>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ChatInterface;
